import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';

const KubernetesDaprComponentsPage = () => {
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);

    const [components, setComponents] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<any>('');

    useEffect(() => {
        const fetchComponents = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/monitoring/dapr-components');
                setComponents(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching Dapr components:', error);
                setLoading(false);
            }
        };

        fetchComponents();
    }, []);

    // Filter by selected AKS cluster from sidebar
    const clusterFilteredComponents = selectedAKSCluster
        ? components.filter((component: any) => component.cluster === selectedAKSCluster.name)
        : components;

    // Apply text filter on top of cluster filter
    const filteredComponents = clusterFilteredComponents.filter((component: any) =>
        component.name.toLowerCase().includes(filter.toLowerCase()) ||
        component.namespace.toLowerCase().includes(filter.toLowerCase()) ||
        component.cluster.toLowerCase().includes(filter.toLowerCase()) ||
        component.type.toLowerCase().includes(filter.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading Dapr components...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dapr Components</h1>
                    {selectedAKSCluster && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Filtered by: {selectedAKSCluster.name}
                        </p>
                    )}
                </div>
                {selectedAKSCluster && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredComponents.length} of {components.length} total components
                    </div>
                )}
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Filter by name, namespace, cluster or type..."
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cluster</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Version</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredComponents.map((component, index) => (
                            <tr key={`${component.cluster}-${component.namespace}-${component.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{component.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Link
                                        to={`/kubernetes/clusters/${component.cluster}`}
                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        {component.cluster}
                                    </Link>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{component.namespace}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{component.type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{component.version || '�'}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${component.status === 'Healthy'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : component.status === 'Degraded'
                                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                        {component.status || 'Unknown'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredComponents.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {filter ? 'No Dapr components match your filter' :
                            selectedAKSCluster ? `No Dapr components found in ${selectedAKSCluster.name}` :
                                'No Dapr components found'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KubernetesDaprComponentsPage;